import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share, PlusSquare, Upload, CheckCircle2, Cake, CalendarDays, ArrowRight, Home, Sliders, ShieldCheck, EyeOff, Bell, PhoneOff } from "lucide-react";

const SEEN_KEY = "sd_onboarding_v2";

export const isStandalone = () =>
  (typeof window !== "undefined") &&
  (window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true);

export const platform = () => {
  const ua = navigator.userAgent || "";
  if (/iphone|ipad|ipod/i.test(ua)) return "ios";
  if (/android/i.test(ua)) return "android";
  return "desktop";
};

export const InstallSteps = () => {
  const p = platform();
  if (p === "ios") {
    return (
      <ol className="space-y-3 text-sm text-zinc-300">
        <li className="flex items-start gap-3"><span className="step">1</span><span>Tap the <b className="text-zinc-100">Share</b> button <Share className="inline h-4 w-4 text-zinc-400" /> in Safari's toolbar (the box with an up-arrow).</span></li>
        <li className="flex items-start gap-3"><span className="step">2</span><span>Scroll down and tap <b className="text-zinc-100">Add to Home Screen</b> <PlusSquare className="inline h-4 w-4 text-zinc-400" />.</span></li>
        <li className="flex items-start gap-3"><span className="step">3</span><span>Tap <b className="text-zinc-100">Add</b>. Now open Stage Door from your Home Screen — that's what makes notifications work.</span></li>
      </ol>
    );
  }
  if (p === "android") {
    return (
      <ol className="space-y-3 text-sm text-zinc-300">
        <li className="flex items-start gap-3"><span className="step">1</span><span>Tap the <b className="text-zinc-100">⋮ menu</b> in the top-right of Chrome.</span></li>
        <li className="flex items-start gap-3"><span className="step">2</span><span>Tap <b className="text-zinc-100">Install app</b> (or <b className="text-zinc-100">Add to Home screen</b>).</span></li>
        <li className="flex items-start gap-3"><span className="step">3</span><span>Confirm, then open Stage Door from your Home Screen so notifications work.</span></li>
      </ol>
    );
  }
  return (
    <ol className="space-y-3 text-sm text-zinc-300">
      <li className="flex items-start gap-3"><span className="step">1</span><span>Click the <b className="text-zinc-100">install icon</b> in your browser's address bar (a small monitor/＋ icon).</span></li>
      <li className="flex items-start gap-3"><span className="step">2</span><span>Choose <b className="text-zinc-100">Install</b> — Stage Door opens in its own window.</span></li>
    </ol>
  );
};

const HowItWorks = () => (
  <ul className="space-y-3 text-sm text-zinc-300">
    <li className="flex items-start gap-3"><Upload className="mt-0.5 h-5 w-5 shrink-0 text-[#E0575B]" /><span><b className="text-zinc-100">Upload a screenshot</b> — a tour sheet fills in the tour details for you, and a <b className="text-zinc-100">schedule screenshot drops your shifts straight onto the calendar</b> automatically.</span></li>
    <li className="flex items-start gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" /><span><b className="text-zinc-100">Mark tours complete</b> — after a tour, add your notes and mark it done. You'll get a gentle reminder if you forget.</span></li>
    <li className="flex items-start gap-3"><Cake className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" /><span><b className="text-zinc-100">Celebrations</b> — save guests' birthdays & anniversaries and get a heads-up when one lands on a tour day.</span></li>
    <li className="flex items-start gap-3"><CalendarDays className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400" /><span><b className="text-zinc-100">Calendar & Next Shift</b> — your schedule at a glance, always up to date.</span></li>
    <li className="flex items-start gap-3"><PhoneOff className="mt-0.5 h-5 w-5 shrink-0 text-[#E0575B]" /><span><b className="text-zinc-100">Call Outs</b> — need to call out sick, late or FMLA? Tap the <b className="text-zinc-100">☰ menu → Call Outs</b>, or hit <b className="text-zinc-100">Call out</b> on any today/tomorrow shift. It tracks your <b className="text-zinc-100">points</b>, your <b className="text-zinc-100">rolling 12-month FMLA</b> hours, and can drop the day off your schedule.</span></li>
  </ul>
);

const SettingsTips = () => (
  <ul className="space-y-3 text-sm text-zinc-300">
    <li className="flex items-start gap-3"><Upload className="mt-0.5 h-5 w-5 shrink-0 text-[#E0575B]" /><span><b className="text-zinc-100">Bring in your history</b> — got past tours in a spreadsheet? In <b className="text-zinc-100">Settings → Data cleanup</b> upload a CSV to bulk-import them. Already track your <b className="text-zinc-100">points or FMLA</b> elsewhere? <b className="text-zinc-100">Settings → Import Call-Outs</b> pulls that history in too (both have an undo).</span></li>
    <li className="flex items-start gap-3"><Bell className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400" /><span><b className="text-zinc-100">Notifications & quiet hours</b> — pick what buzzes you (celebrations, post-tour reminders) and set a do-not-disturb window.</span></li>
    <li className="flex items-start gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-emerald-400" /><span><b className="text-zinc-100">App Lock</b> — add a passcode + Face ID on top of your sign-in.</span></li>
    <li className="flex items-start gap-3"><EyeOff className="mt-0.5 h-5 w-5 shrink-0 text-amber-400" /><span><b className="text-zinc-100">Financial Privacy</b> — hide your tips so you can safely show a screen to someone. Plus light/dark and Apple Calendar sync all live in Settings.</span></li>
  </ul>
);

export function OnboardingDialog() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const installed = isStandalone();
  // If already installed, skip straight to the "How it works" step.
  const steps = installed ? ["how", "settings"] : ["install", "how", "settings"];

  useEffect(() => {
    if (!localStorage.getItem(SEEN_KEY)) setOpen(true);
    const replay = () => { setStep(0); setOpen(true); };
    window.addEventListener("sd:onboarding", replay);
    return () => window.removeEventListener("sd:onboarding", replay);
  }, []);

  const finish = () => {
    localStorage.setItem(SEEN_KEY, "1");
    setOpen(false);
  };

  const current = steps[step];
  const isLast = step === steps.length - 1;

  const meta = {
    install: { icon: Home, title: "Add Stage Door to your Home Screen", desc: "One-time setup — it makes the app feel native and enables notifications." },
    how: { icon: ArrowRight, title: "Welcome to Stage Door 👋", desc: "Here's the quick tour." },
    settings: { icon: Sliders, title: "A few things in Settings", desc: "Worth a peek — especially importing your past tours." },
  }[current];
  const HeadIcon = meta.icon;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(); }}>
      <DialogContent className="max-w-md border-zinc-800 bg-zinc-950" data-testid="onboarding-dialog">
        <div className="mb-1 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#9B2226]/15 text-[#E0575B]">
            <HeadIcon className="h-5 w-5" />
          </div>
          <div>
            <h2 className="font-display text-lg font-800 tracking-tight text-zinc-50">{meta.title}</h2>
            <p className="text-xs text-zinc-400">{meta.desc}</p>
          </div>
        </div>

        <div className="py-2">
          {current === "install" ? <InstallSteps /> : current === "how" ? <HowItWorks /> : <SettingsTips />}
        </div>

        <div className="mt-2 flex items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {steps.map((s, i) => (
              <span key={s} className={`h-1.5 w-1.5 rounded-full ${i === step ? "bg-[#E0575B]" : "bg-zinc-700"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={finish} className="text-zinc-400 hover:text-zinc-200" data-testid="onboarding-skip">Skip</Button>
            {isLast ? (
              <Button onClick={finish} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="onboarding-done">Got it</Button>
            ) : (
              <Button onClick={() => setStep((s) => s + 1)} className="bg-[#9B2226] text-white hover:bg-[#7A1A1E]" data-testid="onboarding-next">Next <ArrowRight className="ml-1 h-4 w-4" /></Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
